import { Alert, Box, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

// The live dashboard's Warnings tab, after the graph tabs: the warnings and
// critical warnings that come with the test's data (missing sensors,
// calibration, plate angle...). The tab's count keeps the colours of the
// warning bar it replaced: green with none, orange with warnings, red when any
// is critical. It pulses until the tab has been opened for the box on screen.

export const WARNING_COLORS = { none: '#2e9b33', warning: 'orange', critical: 'red' };

export function warningLevel(critical = [], warnings = []) {
  if (critical.length) return 'critical';
  if (warnings.length) return 'warning';
  return 'none';
}

// What a screen reader says for the tab, e.g. "Warnings, 3 (1 critical)".
export function warningsTabName(critical = [], warnings = []) {
  const count = critical.length + warnings.length;
  return `Warnings, ${count}${critical.length ? ` (${critical.length} critical)` : ''}`;
}

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.18); }
`;

export function WarningsTabLabel({ critical = [], warnings = [], unread = false, loading = false }) {
  const count = critical.length + warnings.length;
  return (
    <Stack direction='row' alignItems='center' spacing={0.75} component='span'>
      <span>Warnings</span>
      {!loading && (
        <Box
          component='span'
          aria-hidden='true'
          sx={{
            minWidth: 20,
            height: 20,
            px: 0.75,
            borderRadius: 10,
            backgroundColor: WARNING_COLORS[warningLevel(critical, warnings)],
            color: '#FFFFFF',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: unread && count ? `${pulse} 1.6s ease-in-out infinite` : 'none',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          {count}
        </Box>
      )}
    </Stack>
  );
}

// The tab's content, in the graph area: critical warnings first, then the rest.
function WarningsPanel({ critical = [], warnings = [] }) {
  return (
    <Box sx={{ height: '100%', overflowY: 'auto', pr: 0.5 }}>
      {critical.length + warnings.length === 0 ? (
        <Typography color='text.secondary' sx={{ p: 2 }}>No warnings for this test.</Typography>
      ) : (
        <Stack spacing={1} sx={{ py: 1 }}>
          {critical.map((message, index) => (
            <Alert severity='error' key={`critical-${index}`}>{message}</Alert>
          ))}
          {warnings.map((message, index) => (
            <Alert severity='warning' key={`warning-${index}`}>{message}</Alert>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default WarningsPanel;
